import {
  Box,
  Button,
  Checkbox,
  darken,
  FormControlLabel,
  Menu,
  MenuItem,
  Tab,
  Tabs,
} from "@mui/material";
import React, {
  ChangeEvent,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import "./StoreConfigurationInfo.css";
import { appRoles, config, pageNames } from "../../authConfig";
import { CardContainer } from "../../Common/UI/components/CardContainer";
import { LoadingIndicator } from "../../Common/UI/components/Loading";
import useIsAuthorized from "../../Common/UI/hook/useIsAuthorized";

import { ApplicationLocationContext } from "../../Common/UI/utils/ApplicationState";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { GridLoadingOverlay } from "@mui/x-data-grid";
import { theme } from "../../Common/UI/styles/theme";
import DownloadIcon from "@mui/icons-material/Download";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import styles from "./StoreConfiguration.module.css";
import dayjs from "dayjs";
import useFetchMsal from "../../Common/UI/hook/useFetchMsal";
import { CustomTabPanel } from "../../Common/UI/components/CustomTabPanel";
import useUserRoles from "../../Common/UI/hook/useUserRoles";

const BooleanStatusRenderer = ({ value }: { value: boolean }) =>
  value ? (
    <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
  ) : (
    <CancelOutlinedIcon sx={{ color: theme.palette.error.main }} />
  );

const formatAmericanTimeZone = (dateStr: string): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date) + " CDT";
};

interface IPSchemeData {
  id: number;
  deviceType: string;
  deviceName: string;
  ipAddress: string;
  ipRange: string;
  subnetMask: string;
  gateway: string;
  dns1: string;
  dns2: string;
  modified: boolean;
}

interface IPSchemeUpdate {
  id: number;
  deviceName: string;
  ipAddress: string;
  subnetMask: string;
  gateway: string;
  dns1: string;
  dns2: string;
}

interface MenuItemData {
  locationNumber: string;
  locationName: string;
  hasMenuItems: boolean;
  lastUpdate: string;
}

interface STSConnectionData {
  locationNumber: string;
  locationName: string;
  isConnected: boolean;
  lastUpdate: string;
}

function StoreConfiguration(): ReactElement {
  const { setState } = useContext(ApplicationLocationContext);
  const gridRef = useRef<AgGridReact>(null);
  const saveRef = useRef<HTMLButtonElement>(null);

  //tabs
  const [value, setValue] = useState(0);
  const { roles } = useUserRoles();
  const showPosIPScheme = useIsAuthorized(
    [appRoles.Config.ViewIPScheme],
    roles
  );
  const stsViewMonitoring = useIsAuthorized(
    [appRoles.RestaurantSystems.ViewMonitoring],
    roles
  );
  const stsRefreshMonitoring = useIsAuthorized(
    [appRoles.RestaurantSystems.RefreshMonitoring],
    roles
  );
  const allowEdit = useIsAuthorized([appRoles.Config.EditIPScheme], roles);

  const [loading, setLoading] = useState<boolean>(true);
  const [IPSchemeData, setIPSchemeData] = useState<IPSchemeData[]>();
  const [menuItemData, setMenuItemData] = useState<MenuItemData[]>();
  const [menuItemLoading, setMenuItemLoading] = useState<boolean>(false);
  const [stsConnectionData, setSTSConnectionData] = useState<STSConnectionData[]>();
  const [stsConnectionLoading, setSTSConnectionLoading] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [menuItemLastUpdate, setMenuItemLastUpdate] = useState<string | undefined>();
  const [stsConnectionLastUpdate, setSTSConnectionLastUpdate] = useState<string | undefined>();
  const [menuAnchorElement, setMenuAnchorElement] =
    useState<HTMLElement | null>(null);

  const [colsDef, setColsDef] = useState<ColDef[]>([
    { field: "deviceType", headerName: "Device Type" },
    { field: "ipRange", headerName: "IP Range" },
    { field: "deviceName", headerName: "Device Name" },
    { field: "ipAddress", headerName: "IP Address", editable: allowEdit },
    { field: "subnetMask", headerName: "Subnet Mask", editable: allowEdit },
    { field: "gateway", headerName: "Gateway", editable: allowEdit },
    { field: "dns1", headerName: "DNS 1", editable: allowEdit },
    { field: "dns2", headerName: "DNS 2", editable: allowEdit },
  ]);

  useEffect(() => {
    setColsDef((prevCols) =>
      prevCols.map((col) =>
        ["ipAddress", "subnetMask", "gateway", "dns1", "dns2"].includes(
          col.field!
        )
          ? { ...col, editable: allowEdit }
          : col
      )
    );
    gridRef.current?.api.refreshHeader();
  }, [allowEdit]);

  const execute = useFetchMsal();

  const handleExportAsCsvButtonClick = () => {
    gridRef.current?.api.exportDataAsCsv({
      fileName: `export_${dayjs(dayjs())
        .format("YYYY_MM_DD_HH_mm_ss")
        .toString()}`,
    });
  };

  const fetchStoreConfigurationInfo = async () => {
    try {
      setLoading(true);
      const url = config.rootAPIList.storeConfiguration;
      const response = await execute("GET", `${config.rootAPIUrl}${url}`);

      const mappedData: IPSchemeData[] = response.map((item: any) => ({
        id: item.id,
        deviceType: item.deviceType,
        deviceName: item.deviceName,
        ipAddress: item.ipAddress,
        ipRange: item.ipRange,
        subnetMask: item.subnetMask,
        gateway: item.gateway,
        dns1: item.dnS1,
        dns2: item.dnS2,
        modified: false,
      }));

      setIPSchemeData(mappedData);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async () => {
    try {
      setMenuItemLoading(true);
      const url = config.rootAPIList.oracleStsMenuItems;
      const response = await execute("GET", `${config.rootAPIUrl}${url}`);
      setMenuItemData(response);
      setMenuItemLastUpdate(response[0]?.lastUpdate);
    } catch (error) {
      console.error(error);
    } finally {
      setMenuItemLoading(false);
    }
  };

  const fetchSTSConnections = async () => {
    try {
      setSTSConnectionLoading(true);
      const url = config.rootAPIList.oracleStsConnections;
      const response = await execute("GET", `${config.rootAPIUrl}${url}`);
      setSTSConnectionData(response);
      setSTSConnectionLastUpdate(response[0]?.lastUpdate);
    } catch (error) {
      console.error(error);
    } finally {
      setSTSConnectionLoading(false);
    }
  };

  const handleMonitoringRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);

    try {
      const url = config.rootAPIList.oracleStsMonitoringRefresh;
      const result = await execute("PUT", `${config.rootAPIUrl}${url}`);
      if (result?.status?.toLowerCase() === 'success') {
        fetchMenuItems();
        fetchSTSConnections();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 1 && !menuItemData) fetchMenuItems();
    if (newValue === 2 && !stsConnectionData) fetchSTSConnections();
  };

  const handleColumnsMenuChange = (e: ChangeEvent<HTMLInputElement>) => {
    setColsDef((prev) =>
      prev.map((col: ColDef) =>
        col.field === e.target.name ? { ...col, hide: !col.hide } : col
      )
    );
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    gridRef.current?.api.stopEditing();
    let modifiedRows: IPSchemeUpdate[] = [];
    gridRef.current?.api.forEachNode((node) => {
      if (node.data.modified) {
        modifiedRows.push({
          id: node.data.id,
          deviceName: node.data.deviceName,
          ipAddress: node.data.ipAddress,
          subnetMask: node.data.subnetMask,
          gateway: node.data.gateway,
          dns1: node.data.dns1,
          dns2: node.data.dns2,
        });
      }
    });

    try {
      const url = config.rootAPIList.updateStoreConfigurationIpScheme;
      await execute("PUT", `${config.rootAPIUrl}${url}`, modifiedRows);
      await fetchStoreConfigurationInfo();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setState(() => ({ path: [pageNames.storeConfiguration] }));
    fetchStoreConfigurationInfo();
  }, []);

  return (
    <div className={styles.storeConfigurationContainer}>
      <CardContainer title="Restaurant Systems" className="store-info">
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={value} onChange={handleTabChange} aria-label="">
              <Tab
                sx={showPosIPScheme ? {} : { display: "none" }}
                label="POS IP Scheme"
                id={`simple-tab-1`}
                aria-controls={`simple-tabpanel-0`}
              />
              <Tab
                label="Menu Item"
                id={`simple-tab-2`}
                aria-controls={`simple-tabpanel-1`}
                sx={stsViewMonitoring ? {} : { display: "none" }}
              />
              <Tab
                label="STS Connectivity"
                id={`simple-tab-3`}
                aria-controls={`simple-tabpanel-2`}
                sx={stsViewMonitoring ? {} : { display: "none" }}
              />
            </Tabs>
          </Box>

          <div className="spacer-small"></div>

          {/* panel 1 */}
          {showPosIPScheme ? (
            <CustomTabPanel value={value} index={0}>
              <LoadingIndicator show={loading}>
                {(IPSchemeData?.length ?? 0) > 0 ? (
                  <>
                    <div>
                      <Box sx={{ display: "flex", mb: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={<ViewWeekIcon />}
                          sx={{
                            mb: 1,
                            mr: 1,
                            backgroundColor: theme.palette.secondary.main,
                            "&:hover": {
                              backgroundColor: darken(
                                theme.palette.secondary.main,
                                0.1
                              ),
                            },
                          }}
                          disableElevation
                          onClick={(e) => setMenuAnchorElement(e.currentTarget)}
                        >
                          Columns
                        </Button>
                        <Button
                          sx={{
                            mb: 1,
                            mr: 1,
                            width: "200px",
                            backgroundColor: theme.palette.secondary.main,
                            "&:hover": {
                              backgroundColor: darken(
                                theme.palette.secondary.main,
                                0.1
                              ),
                            },
                          }}
                          startIcon={<DownloadIcon />}
                          onClick={handleExportAsCsvButtonClick}
                        >
                          Export as CSV
                        </Button>
                        {allowEdit && (
                          <Button
                            sx={{
                              mb: 1,
                              ml: "auto",
                              width: "200px",
                              backgroundColor: theme.palette.secondary.main,
                              "&:hover": {
                                backgroundColor: darken(
                                  theme.palette.secondary.main,
                                  0.1
                                ),
                              },
                            }}
                            startIcon={<SaveIcon />}
                            onClick={handleSaveChanges}
                            ref={saveRef}
                          >
                            Save changes
                          </Button>
                        )}
                        <Menu
                          open={Boolean(menuAnchorElement)}
                          anchorEl={menuAnchorElement}
                          onClose={() => setMenuAnchorElement(null)}
                        >
                          {colsDef.map((col: ColDef) => (
                            <MenuItem key={col.field}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!col.hide}
                                    name={col.field}
                                    onChange={handleColumnsMenuChange}
                                  />
                                }
                                label={col.headerName}
                              />
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    </div>
                    <div id={styles.devicesGrid} className="ag-theme-quartz">
                      <AgGridReact
                        columnDefs={colsDef}
                        rowData={IPSchemeData || []}
                        onCellValueChanged={(e) => {
                          e.data.modified = true;
                        }}
                        loading={loading}
                        loadingOverlayComponent={GridLoadingOverlay}
                        ref={gridRef}
                        pagination
                        paginationPageSize={20}
                        defaultColDef={{
                          headerClass: "tableHeader",
                          filter: "agTextColumnFilter",
                          filterParams: {
                            buttons: ["apply", "clear"],
                            closeOnApply: true,
                            maxNumConditions: 4,
                          },
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </LoadingIndicator>
            </CustomTabPanel>
          ) : (
            <></>
          )}

          {/* panel 2 - Menu Item */}
          {stsViewMonitoring ? (
            <CustomTabPanel value={value} index={1}>
              <LoadingIndicator show={menuItemLoading}>
                <Box sx={{ display: "flex", mb: 1, alignItems: "center" }}>
                  {stsRefreshMonitoring && (
                    <Button
                      variant="contained"
                      sx={{
                        mb: 1,
                        backgroundColor: theme.palette.secondary.main,
                        "&:hover": {
                          backgroundColor: darken(
                            theme.palette.secondary.main,
                            0.1
                          ),
                        },
                      }}
                      disableElevation
                      disabled={isRefreshing}
                      onClick={handleMonitoringRefresh}
                    >
                      Refresh
                    </Button>
                  )}
                  {isRefreshing && (
                    <p style={{ margin: "0 0 8px 12px" }}>
                      Please wait while the latest data is being fetched...
                    </p>
                  )}
                  {!isRefreshing && menuItemLastUpdate && (
                    <p style={{ margin: "0 0 8px 12px", color: "gray", fontSize: "0.85rem" }}>
                      Last updated: {formatAmericanTimeZone(menuItemLastUpdate)}
                    </p>
                  )}
                </Box>
                <div id={styles.devicesGrid} className="ag-theme-quartz">
                  <AgGridReact
                    columnDefs={[
                      { field: "locationNumber", headerName: "Location #" },
                      { field: "locationName", headerName: "Location Name" },
                      {
                        field: "hasMenuItems",
                        headerName: "Has Menu Items",
                        cellRenderer: BooleanStatusRenderer,
                        sort: "asc",
                      },
                    ]}
                    rowData={menuItemData || []}
                    loading={menuItemLoading}
                    loadingOverlayComponent={GridLoadingOverlay}
                    pagination
                    paginationPageSize={20}
                    defaultColDef={{
                      headerClass: "tableHeader",
                      filter: "agTextColumnFilter",
                      filterParams: {
                        buttons: ["apply", "clear"],
                        closeOnApply: true,
                        maxNumConditions: 4,
                      },
                    }}
                  />
                </div>
              </LoadingIndicator>
            </CustomTabPanel>
          ) : (
            <></>
          )}

          {/* panel 3 - STS Connectivity */}
          {stsViewMonitoring ? (
            <CustomTabPanel value={value} index={2}>
              <LoadingIndicator show={stsConnectionLoading}>
                <Box sx={{ display: "flex", mb: 1, alignItems: "center" }}>
                  {stsRefreshMonitoring && (
                    <Button
                      variant="contained"
                      sx={{
                        mb: 1,
                        backgroundColor: theme.palette.secondary.main,
                        "&:hover": {
                          backgroundColor: darken(
                            theme.palette.secondary.main,
                            0.1
                          ),
                        },
                      }}
                      disableElevation
                      disabled={isRefreshing}
                      onClick={handleMonitoringRefresh}
                    >
                      Refresh
                    </Button>
                  )}
                  {isRefreshing && (
                    <p style={{ margin: "0 0 8px 12px" }}>
                      Please wait while the latest data is being fetched...
                    </p>
                  )}
                  {!isRefreshing && stsConnectionLastUpdate && (
                    <p style={{ margin: "0 0 8px 12px", color: "gray", fontSize: "0.85rem" }}>
                      Last updated: {formatAmericanTimeZone(stsConnectionLastUpdate)}
                    </p>
                  )}
                </Box>
                <div id={styles.devicesGrid} className="ag-theme-quartz">
                  <AgGridReact
                    columnDefs={[
                      { field: "locationNumber", headerName: "Location #" },
                      { field: "locationName", headerName: "Location Name" },
                      {
                        field: "isConnected",
                        headerName: "Connected",
                        cellRenderer: BooleanStatusRenderer,
                        sort: "asc",
                      },
                    ]}
                    rowData={stsConnectionData || []}
                    loading={stsConnectionLoading}
                    loadingOverlayComponent={GridLoadingOverlay}
                    pagination
                    paginationPageSize={20}
                    defaultColDef={{
                      headerClass: "tableHeader",
                      filter: "agTextColumnFilter",
                      filterParams: {
                        buttons: ["apply", "clear"],
                        closeOnApply: true,
                        maxNumConditions: 4,
                      },
                    }}
                  />
                </div>
              </LoadingIndicator>
            </CustomTabPanel>
          ) : (
            <></>
          )}
        </Box>
      </CardContainer>
    </div>
  );
}
export default StoreConfiguration;
